import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Check,
  ArrowRight,
  Heart,
  Users,
  Shield,
  Sparkles,
  Dumbbell,
  Laptop,
  Smartphone,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { SERVICES, FAQ_ITEMS, ABOUT } from "@/lib/content";
import { BRAND } from "@/lib/content";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import SEO, { faqJsonLd } from "@/components/SEO";

const ICONS = { Laptop, Sparkles, Dumbbell, Heart, Users, Shield };

function Icon({ name, className = "text-[#E8A6B8]", size = 24 }) {
  const C = ICONS[name] || Heart;
  return <C size={size} strokeWidth={1.5} className={className} />;
}

const WHY_PILLARS = [
  { title: "Women-only coaching", icon: "Users" },
  { title: "Supportive, not intimidating", icon: "Heart" },
  { title: "Realistic & sustainable", icon: "Shield" },
  { title: "Beginner friendly", icon: "Sparkles" },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    // Wait for layout/images to settle so offset is accurate
    const id = hash.replace("#", "");
    const target = document.getElementById(id);
    if (target) {
      // Small delay to let the page paint
      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        // Subtle highlight ring
        target.classList.add("hfh-pulse");
        setTimeout(() => target.classList.remove("hfh-pulse"), 1800);
      }, 120);
    }
  }, [hash]);

  return (
    <div data-testid="services-page" className="bg-[#F6EFE9]">
      <Hero />
      <ServicesGrid />
      <WhyTrain />
      <Testimonials />
      <FAQ />
      <FinalCTA />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="max-w-4xl mx-auto text-center fade-in">
          <p className="eyebrow mb-5" data-testid="services-eyebrow">Services</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#3A3A3A]">
            Women's Strength Coaching <br className="hidden sm:block" />
            <span className="italic text-[#E8A6B8]">Designed Around Real Life</span>
          </h1>
          <p className="heart-divider mt-5">♡</p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-2xl mx-auto">
            Helping women build strength, confidence and consistency through
            realistic coaching that fits around your lifestyle.
          </p>

          {/* Limited monthly intake — soft premium trust pill */}
          <div className="mt-7 flex justify-center fade-in" style={{ animationDelay: "150ms" }}>
            <span
              className="inline-flex items-center gap-2.5 bg-white/85 backdrop-blur-sm border border-[#F3C9D3]/70 rounded-full pl-3 pr-5 py-2 shadow-[0_8px_22px_rgba(232,166,184,0.16)]"
              data-testid="services-intake-pill"
              aria-label="Currently accepting a limited number of coaching clients each month"
            >
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#E8A6B8] opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E8A6B8]" />
              </span>
              <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
                Currently Accepting
              </span>
              <span className="text-[#E8A6B8]" aria-hidden>·</span>
              <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                Limited Monthly Intake
              </span>
            </span>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/booking"
              className="btn-pink"
              data-testid="hero-book-consultation"
            >
              Book Consultation
            </Link>
            <a
              href="#services"
              className="btn-outline"
              data-testid="hero-explore-coaching"
            >
              Explore Coaching
            </a>
          </div>
        </div>
      </div>
      {/* Soft decorative gradient blob */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#E8A6B8]/20 blur-3xl"
      />
    </section>
  );
}

/* ---------- SERVICES GRID ---------- */
function ServicesGrid() {
  return (
    <section id="services" className="py-20 lg:py-28 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Coaching Options</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Find the right <span className="italic text-[#E8A6B8]">fit</span> for you
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>

        {/* Subtle client-app explainer — positions Strong With Maya cleanly */}
        <aside
          className="mt-14 max-w-3xl mx-auto bg-white rounded-[1.75rem] border border-[#F3C9D3]/40 px-7 py-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-[0_8px_22px_rgba(232,166,184,0.12)]"
          data-testid="client-app-note"
        >
          <span className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 flex-shrink-0">
            <Smartphone size={20} strokeWidth={1.5} className="text-[#E8A6B8]" />
          </span>
          <div className="flex-1">
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
              For Active Clients
            </p>
            <p className="mt-1.5 text-sm text-[#5C5C5C] leading-relaxed">
              Active clients receive their training plan, check-ins and
              progress tracking through the{" "}
              <strong className="text-[#3A3A3A]">{BRAND.clientAppName}</strong>{" "}
              coaching app.
            </p>
          </div>
          <a
            href={BRAND.clientAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-xs whitespace-nowrap"
            data-testid="services-client-login"
          >
            Client Login <ArrowRight size={13} />
          </a>
        </aside>
      </div>
    </section>
  );
}

function ServiceCard({ service, index }) {
  return (
    <article
      id={service.id}
      className="group relative bg-white rounded-[2rem] overflow-hidden border border-[#F3C9D3]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(232,166,184,0.25)] scroll-mt-28"
      data-testid={`service-card-${service.id}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="aspect-[16/11] overflow-hidden bg-[#F3C9D3]/20">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-8 flex flex-col">
        <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 self-start mb-4">
          <Icon name={service.icon} size={22} />
        </div>
        <h3 className="font-serif-display text-2xl text-[#3A3A3A] leading-tight">
          {service.title}
        </h3>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E8A6B8] mt-1 font-semibold">
          {service.subtitle}
        </p>
        <p className="mt-5 text-sm leading-relaxed text-[#5C5C5C]">
          {service.description}
        </p>
        <ul className="mt-6 space-y-2.5">
          {service.included.map((it) => (
            <li
              key={it}
              className="flex items-start gap-2.5 text-sm text-[#3A3A3A]"
            >
              <span className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F3C9D3]/60">
                <Check size={11} className="text-[#3A3A3A]" strokeWidth={2.5} />
              </span>
              {it}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs italic text-[#3A3A3A]/60">{service.forWho}</p>
        <Link
          to="/booking"
          className="btn-pink mt-7 w-full text-center text-xs"
          data-testid={`service-cta-${service.id}`}
        >
          {service.cta} <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}

/* ---------- WHY TRAIN WITH ME ---------- */
function WhyTrain() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3]">
            <img
              src={ABOUT.image}
              alt="Maya - women's strength coach"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="eyebrow mb-4">Why Train With Me</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
            More Than Just <br />
            <span className="italic text-[#E8A6B8]">A Workout Plan</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            My coaching is designed to help women build strength, confidence and
            consistency in a realistic way that works around real life.
          </p>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            Whether you're a complete beginner, rebuilding after having
            children, or wanting to feel stronger mentally and physically, my
            goal is to create a supportive environment where women feel
            empowered — never intimidated.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-5">
            {WHY_PILLARS.map((p) => (
              <div
                key={p.title}
                className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-2xl px-4 py-4 border border-white/40"
              >
                <span className="flex-shrink-0 inline-flex p-2.5 rounded-xl bg-[#F3C9D3]/60">
                  <Icon name={p.icon} size={18} />
                </span>
                <p className="text-sm font-medium text-[#3A3A3A] leading-tight">
                  {p.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- TESTIMONIALS ---------- */
function Testimonials() {
  return (
    <section className="py-20 lg:py-28">
      <TestimonialsCarousel />
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  return (
    <section className="py-20 lg:py-28 bg-[#F6EFE9]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FAQ</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Questions, <span className="italic text-[#E8A6B8]">answered</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 px-6 sm:px-8 divide-y divide-[#F3C9D3]/30"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-0"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger
                className="text-left text-[#3A3A3A] font-medium text-base sm:text-lg py-6 hover:no-underline"
                data-testid={`faq-trigger-${i}`}
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent
                className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed pb-6"
                data-testid={`faq-content-${i}`}
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative text-center"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-10 py-20 sm:py-24">
          <p className="eyebrow text-[#3A3A3A]/80 mb-4" data-testid="final-cta-eyebrow">
            Ready When You Are
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight max-w-2xl mx-auto">
            You Deserve To Feel <br />
            <span className="italic">Strong Again</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl mx-auto">
            Strength is not about becoming smaller. It's about becoming more
            confident, capable and resilient.
          </p>
          <Link
            to="/booking"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-10 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
            data-testid="final-cta-book"
          >
            Book Your Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
